import React, { useEffect, useRef, useState } from 'react';
import { Type as TypeIcon, X, Trash2 } from 'lucide-react';
import { loadPdfDocument, AddedText } from '../utils/pdfProcessor';
import { makeAddedTextId } from '../utils/addText';

/**
 * Click-anywhere text placement: click an empty spot on the page to drop a
 * new text box there and start typing (native input, same "notepad" feel as
 * TextEditModal), click an existing box to edit it, hover for a delete
 * button. Deliberately separate from TextEditModal — it doesn't need pdf.js's
 * TextLayer or any hit-testing against existing content at all, so keeping
 * it its own component means nothing here can regress the click/positioning
 * behavior that took several rounds to get right over there.
 */

interface AddTextModalProps {
  sourceBuffer: ArrayBuffer;
  pageIndex: number;
  rotation: number;
  existingTexts: AddedText[];
  onSave: (texts: AddedText[]) => void;
  onClose: () => void;
}

interface Box {
  key: string;
  text: string;
  ux: number; uy: number; // PDF user-space baseline origin
  fontSize: number;
  color: [number, number, number];
}

const SWATCHES: Array<{ label: string; color: [number, number, number] }> = [
  { label: 'Black', color: [0, 0, 0] },
  { label: 'Red', color: [0.8, 0.1, 0.1] },
  { label: 'Blue', color: [0.1, 0.2, 0.75] },
  { label: 'White', color: [1, 1, 1] },
];

function colorToCss([r, g, b]: [number, number, number]): string {
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}

export const AddTextModal: React.FC<AddTextModalProps> = ({
  sourceBuffer, pageIndex, rotation, existingTexts, onSave, onClose,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [defaultFontSize, setDefaultFontSize] = useState(14);
  const [defaultColor, setDefaultColor] = useState<[number, number, number]>([0, 0, 0]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const pdfDoc = await loadPdfDocument(sourceBuffer);
        if (!active) return;
        const page = await pdfDoc.getPage(pageIndex + 1);
        if (!active) return;

        const rot = (page.rotate + rotation) % 360;
        const native = page.getViewport({ scale: 1, rotation: rot });
        const containerW = Math.min(containerRef.current?.clientWidth || 800, 900);
        const scale = Math.max(containerW / native.width, 0.1);
        const viewport = page.getViewport({ scale, rotation: rot });
        viewportRef.current = viewport;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!active) return;

        // Default new boxes to the page's own most common font size (mode,
        // rounded to the nearest point) instead of a fixed guess — a fixed
        // default reads as visibly mismatched against whatever size the rest
        // of that particular page's body text actually is.
        try {
          const textContent = await page.getTextContent();
          const counts = new Map<number, number>();
          for (const item of textContent.items as any[]) {
            if (!item.str || !item.str.trim()) continue;
            const size = Math.round(Math.hypot(item.transform[2], item.transform[3]));
            if (size > 0) counts.set(size, (counts.get(size) || 0) + 1);
          }
          let best = 0, bestCount = 0;
          for (const [size, count] of counts) {
            if (count > bestCount) { best = size; bestCount = count; }
          }
          if (active && best > 0) setDefaultFontSize(best);
        } catch {
          // Keep the built-in default if text content can't be read for any reason.
        }

        setBoxes(existingTexts.map(t => ({
          key: t.id, text: t.text, ux: t.x, uy: t.y, fontSize: t.fontSize, color: t.color,
        })));
      } catch (e: any) {
        console.error('AddTextModal: failed to load this page', e);
        if (active) setError(e?.message || 'Failed to load this page.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceBuffer, pageIndex, rotation]);

  const toScreen = (ux: number, uy: number): { left: number; top: number } => {
    const vp = viewportRef.current;
    if (!vp) return { left: 0, top: 0 };
    const [sx, sy] = vp.convertToViewportPoint(ux, uy);
    return { left: sx, top: sy };
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const vp = viewportRef.current;
    if (!vp || loading) return;
    const target = e.currentTarget.getBoundingClientRect();
    const screenX = e.clientX - target.left;
    const screenY = e.clientY - target.top;
    const [ux, uy] = vp.convertToPdfPoint(screenX, screenY);
    const key = makeAddedTextId();
    setBoxes(prev => [...prev, {
      key, text: '', ux, uy, fontSize: defaultFontSize, color: defaultColor,
    }]);
    setEditingKey(key);
  };

  const commitBox = (key: string, value: string) => {
    if (!value.trim()) {
      setBoxes(prev => prev.filter(b => b.key !== key));
    } else {
      setBoxes(prev => prev.map(b => (b.key === key ? { ...b, text: value } : b)));
    }
    setEditingKey(null);
  };

  const removeBox = (key: string) => {
    setBoxes(prev => prev.filter(b => b.key !== key));
    if (editingKey === key) setEditingKey(null);
  };

  const applyToEditingBox = (patch: Partial<Pick<Box, 'fontSize' | 'color'>>) => {
    if (editingKey) {
      setBoxes(prev => prev.map(b => (b.key === editingKey ? { ...b, ...patch } : b)));
    }
    if (patch.fontSize !== undefined) setDefaultFontSize(patch.fontSize);
    if (patch.color !== undefined) setDefaultColor(patch.color);
  };

  const handleSave = () => {
    const texts: AddedText[] = boxes
      .filter(b => b.text.trim())
      .map(b => ({ id: b.key, text: b.text, x: b.ux, y: b.uy, fontSize: b.fontSize, color: b.color }));
    onSave(texts);
  };

  const activeFontSize = editingKey ? (boxes.find(b => b.key === editingKey)?.fontSize ?? defaultFontSize) : defaultFontSize;
  const activeColor = editingKey ? (boxes.find(b => b.key === editingKey)?.color ?? defaultColor) : defaultColor;
  const scale = viewportRef.current?.scale ?? 1;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--bg-card)', borderRadius: 14, width: 'min(960px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--separator)' }}>
          <TypeIcon size={16} style={{ color: 'var(--accent)' }} />
          <b style={{ fontSize: 14, flex: 1 }}>Add text</b>
          <button className="btn-icon" onClick={onClose} title="Close"><X size={15} /></button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--separator)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            Size
            <input
              type="number" min={6} max={72} value={activeFontSize}
              onChange={e => applyToEditingBox({ fontSize: Math.max(6, Math.min(72, Number(e.target.value) || 14)) })}
              style={{ width: 52 }}
            />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {SWATCHES.map(s => (
              <button
                key={s.label}
                title={s.label}
                onClick={() => applyToEditingBox({ color: s.color })}
                style={{
                  width: 20, height: 20, borderRadius: '50%', cursor: 'pointer',
                  background: colorToCss(s.color),
                  border: activeColor.every((c, i) => c === s.color[i]) ? '2px solid var(--accent)' : '1px solid var(--separator)',
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, overflowY: 'auto' }}>
          {error ? (
            <p style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</p>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', margin: 0 }}>
                Click anywhere on the page to add text there — type, then click elsewhere to place another.
              </p>
              <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', minHeight: loading ? 300 : undefined }}>
                {loading && <div className="spinner" style={{ marginTop: 100 }} />}
                <div style={{ position: 'relative', visibility: loading ? 'hidden' : 'visible' }} onClick={handleCanvasClick}>
                  <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.12)', cursor: 'text' }} />
                  {boxes.map(b => {
                    const { left, top } = toScreen(b.ux, b.uy);
                    const screenSize = b.fontSize * scale;
                    return editingKey === b.key ? (
                      <input
                        key={b.key}
                        autoFocus
                        defaultValue={b.text}
                        placeholder="Type…"
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          left, top: top - screenSize,
                          minWidth: 60,
                          fontSize: screenSize,
                          lineHeight: 1,
                          color: colorToCss(b.color),
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          border: '1px dashed var(--accent)', outline: 'none',
                          background: 'rgba(255,255,255,0.9)', padding: '1px 2px', margin: 0,
                          zIndex: 2,
                        }}
                        onBlur={e => commitBox(b.key, e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                          if (e.key === 'Escape') { e.preventDefault(); commitBox(b.key, b.text); }
                        }}
                      />
                    ) : (
                      <div
                        key={b.key}
                        onClick={e => { e.stopPropagation(); setEditingKey(b.key); }}
                        className="add-text-box"
                        style={{
                          position: 'absolute',
                          left, top: top - screenSize,
                          fontSize: screenSize,
                          lineHeight: 1,
                          color: colorToCss(b.color),
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          whiteSpace: 'pre',
                          cursor: 'pointer',
                          padding: '1px 2px',
                        }}
                      >
                        {b.text}
                        <button
                          className="add-text-delete"
                          title="Delete"
                          onClick={e => { e.stopPropagation(); removeBox(b.key); }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderTop: '1px solid var(--separator)' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1 }}>
            {boxes.length > 0 ? `${boxes.length} text box${boxes.length === 1 ? '' : 'es'}` : 'No text added yet'}
          </span>
          <button className="btn-sm btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-sm btn-primary" onClick={handleSave} disabled={boxes.length === 0 && existingTexts.length === 0}>
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};
